import {apiClient} from "../../../utils/axios";
import {useDispatch} from "react-redux";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useState} from "react";
import {showSnackbar} from "../../../api/snackbarSlice";
import {AxiosError} from "axios";
import {isString} from "lodash";

const FIELD_ALIASES = {
  endpoint_id: 'party_tax_scheme.company_id',
};

const pascalSegmentToSnake = (segment) =>
  segment.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

const normalizeFieldKey = (key) => {
  const snakeCaseKey = key.split('.').map(pascalSegmentToSnake).join('.');
  return FIELD_ALIASES[snakeCaseKey] || FIELD_ALIASES[key] || snakeCaseKey;
};

const normalizeApiErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  return Object.entries(errors).reduce((acc, [key, message]) => {
    if (message) {
      acc[normalizeFieldKey(key)] = message;
    }
    return acc;
  }, {});
};

const extractErrorMessage = (data) => {
  if (!data) {
    return 'An unexpected error occurred';
  }
  if (data.message) {
    return data.message;
  }
  if (data.error) {
    return data.error;
  }
  if (data.errors) {
    const firstMessage = Object.values(data.errors)[0];
    if (firstMessage) {
      return firstMessage;
    }
  }
  return 'An unexpected error occurred';
};

const createBusinessProfile = async ({payload, isEdit}) => {
    if (isEdit) {
        const { data } = await apiClient.put('/business-profiles/' + payload.profile_id, payload);
        return data;
    } else {
        const response = await apiClient.post('/business-profiles', payload);
        return response.data;
    }
}

export const useCreateBusinessProfileHandler = ({ onSuccess }) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [error, setError] = useState(null);

    const mutation = useMutation({
        mutationFn: createBusinessProfile,
        onSuccess: (data, variables, context) => {
            dispatch(showSnackbar({ message: 'Business profile created/updated successfully.', variant: 'success' }));
            queryClient.invalidateQueries({queryKey: ['business_profiles']});
            onSuccess && onSuccess(variables);
        },
        onError: (error) => {
            if (error instanceof AxiosError) {
                const status = error.response?.status;
                const data = error.response?.data;

                if (status === 400 || status === 409) {
                    const normalizedErrors = normalizeApiErrors(data?.errors);
                    if (normalizedErrors && Object.keys(normalizedErrors).length > 0) {
                        setError(normalizedErrors);
                        return;
                    }
                }

                dispatch(showSnackbar({ message: extractErrorMessage(data), severity: 'error'}));
            } else {
                dispatch(
                    showSnackbar({
                        message: isString(error.message) ? error.message : JSON.stringify(error.message),
                        severity: 'error',
                    }),
                );
            }
        },
    });

    const onSubmit = async (payload, isEdit) => {
        setError(null);
        mutation.mutate({payload, isEdit});
    };

    return {
        data: mutation.data,
        isError: mutation.isError,
        error: error,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        onSubmit: onSubmit,
    };
};